import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Building2, LogOut, Megaphone, Menu, Settings, Wrench, Users } from "lucide-react";
import { useMgmtBrand } from "@/lib/useMgmtBrand";
import { useStayPut } from "@/lib/useStayPut";
import InstallAppButton from "@/components/InstallAppButton";
import PushToggle from "@/components/PushToggle";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { useHeaderHeightVar } from "@/lib/useHeaderHeightVar";
import { api } from "@/lib/api";
import "@/styles/management.css";
import { PortalInvitationsDialog } from "@/components/onboarding/PortalInvitationsDialog";

// Persisted theme preference. Admins can pick between "green"
// (Bungalow-matched emerald look) and "blue" (the old slate/sky-blue
// Management look — now the default for anyone who hasn't picked yet).
// Persisted per browser via localStorage so a page refresh keeps the
// choice. Extra admins the owner invites can each pick their own.
// Managed from Settings & tools → Appearance (moved out of the header).
const THEME_KEY = "mgmt_theme_pref";

/**
 * Management USA NJ standalone layout — the admin lands here after
 * signing in through /managementusa/login and only ever sees the
 * Management USA NJ page (no Catskills Pool Supplies sidebar tabs).
 */
export default function MgmtLayout() {
  const { user, logout } = useAuth();
  const [invitationsOpen, setInvitationsOpen] = useState(() => new URLSearchParams(window.location.search).get("invitations") === "1");
  const isTenant = user && (user.role === "mgmt" || user.is_mgmt === true);
  const bizName = isTenant ? (user.business_name || user.name || "My business") : "Management USA NJ";
  useMgmtBrand(`${bizName} — Admin`);
  useStayPut();
  const nav = useNavigate();
  const headerRef = useHeaderHeightVar();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || "blue");
  const isAdmin = user?.role === "admin" || user?.role === "mgmt";
  const isDemo = user?.is_demo === true;

  useEffect(() => {
    document.body.classList.add("mgmt-portal-shell");
    return () => document.body.classList.remove("mgmt-portal-shell");
  }, []);

  // Server-persisted theme preference. When /auth/me returns a `theme_pref`,
  // apply it and mirror to localStorage so this browser also snaps to the
  // right theme on the very next paint. Users who never toggled fall back
  // to the localStorage default (or "green" on a fresh browser).
  useEffect(() => {
    const pref = user?.theme_pref;
    if (pref === "green" || pref === "blue") {
      localStorage.setItem(THEME_KEY, pref);
      setTheme(pref);
    }
  }, [user?.theme_pref]);

  // Radix dialogs mount via document.body portal, so a wrapper class
  // never reaches them. Tag <body> instead while this portal is mounted
  // — that way "New ticket", "Add expense", "Auto-invoice" dialogs all
  // pick up the same theme.
  useEffect(() => {
    if (theme === "blue") {
      // Blue mode falls back to the default :root sky-blue palette —
      // just make sure any lingering emerald class is cleared.
      document.body.classList.remove("theme-emerald");
      return;
    }
    document.body.classList.add("theme-emerald");
    return () => { document.body.classList.remove("theme-emerald"); };
  }, [theme]);

  const setThemePref = (next) => {
    localStorage.setItem(THEME_KEY, next);
    setTheme(next);
    // Fire-and-forget server-side persist so the choice follows the
    // user to new devices / new browsers. Local state already flipped
    // above; a network hiccup can't undo it (worst case: next full
    // login on another device shows the old theme).
    api.put("/auth/me/theme", { theme: next }).catch(() => {});
  };

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    await logout();
    nav("/managementusa", { replace: true });
  };

  // Owner has the full Issues board on the Overview page — everyone else
  // (extra admins the owner invites, secretaries, mgmt-portal tenants)
  // gets a quick "Report issue" button in the header so they can submit
  // bug reports / upgrade requests that land in the owner's inbox.
  const isOwner = user?.is_owner === true;

  // Header styles per theme. Blue mode reproduces the pre-recolor dark
  // slate look; green mode is the current white-with-emerald-accent.
  const isBlue = theme === "blue";
  const rootBg = isBlue ? "bg-slate-50" : "bg-emerald-50/40";
  const headerBg = isBlue ? "bg-slate-900 text-slate-50 border-slate-900/40" : "bg-background/95 border-border";
  const brandIconColor = isBlue ? "text-sky-300" : "text-emerald-700";
  const brandTextColor = isBlue ? "text-white" : "text-emerald-800";
  const subLabelColor = isBlue ? "text-slate-400" : "text-muted-foreground";

  return (
    <div className={`min-h-screen mgmt-mobile-shell management-skin management-admin ${rootBg}`} data-testid="management-admin-shell">
      <header ref={headerRef} className={`mgmt-pwa-header sticky top-0 z-30 border-b backdrop-blur ${headerBg}`} data-testid="mgmt-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className={`w-5 h-5 shrink-0 ${brandIconColor}`} />
            <div className="min-w-0">
              <div className={`text-sm sm:text-lg font-black tracking-tighter font-display leading-none uppercase truncate ${brandTextColor}`} data-testid="mgmt-portal-brand">{bizName}</div>
              <div className={`text-[10px] uppercase tracking-widest mt-0.5 ${subLabelColor}`}>Admin portal</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-3">
            {user?.role === "mgmt" && <Button variant="outline" className={isBlue ? "bg-transparent border-slate-600 text-slate-100 hover:bg-slate-800" : ""} onClick={()=>setInvitationsOpen(true)} data-testid="mgmt-invitations-nav"><Users className="h-4 w-4 mr-2"/>Invite</Button>}
            {isAdmin && (
              <Button
                variant="outline"
                size="icon"
                className={`rounded-sm h-9 w-9 ${isBlue ? "bg-transparent border-slate-600 text-slate-100 hover:bg-slate-800" : ""}`}
                onClick={() => nav("/managementusa/admin/settings")}
                title="Settings"
                data-testid="mgmt-settings-btn"
              >
                <Settings className="w-4 h-4" />
              </Button>
            )}
            {isAdmin && !isDemo && (
              <Button
                variant="outline"
                size="icon"
                className={`rounded-sm h-9 w-9 ${isBlue ? "bg-transparent border-slate-600 text-slate-100 hover:bg-slate-800" : ""}`}
                onClick={() => nav("/desktop", { state: { admin: true } })}
                title="Tools (Desktop apps)"
                data-testid="mgmt-tools-btn"
              >
                <Wrench className="w-4 h-4" />
              </Button>
            )}
            {!isOwner && (
              <Button
                variant="outline"
                size="sm"
                className={`rounded-sm ${isBlue ? "bg-transparent border-slate-600 text-slate-100 hover:bg-slate-800" : ""}`}
                onClick={() => setFeedbackOpen(true)}
                data-testid="mgmt-report-issue"
              >
                <Megaphone className="w-4 h-4 mr-2" /> Report issue
              </Button>
            )}
            <InstallAppButton
              manifest="/manifest-mgmt.json"
              appleIcon="/mgmt-apple-touch.png"
              appName="Management USA NJ"
              iosTitle="Management"
              className={`h-9 ${isBlue ? "bg-transparent border-slate-600 text-slate-100 hover:bg-slate-800" : ""}`}
            />
            <PushToggle client={api} base="/push" targetUrl="/managementusa/admin" testidPrefix="mgmt-header-push" />
            <div className="hidden sm:block text-right">
              <div className={`text-[10px] uppercase tracking-widest ${subLabelColor}`}>Signed in as</div>
              <div className="text-sm font-medium" data-testid="mgmt-me-label">{user?.name || user?.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className={`rounded-sm ${isBlue ? "bg-transparent border-slate-600 text-slate-100 hover:bg-slate-800" : ""}`}
              onClick={handleLogout}
              data-testid="mgmt-logout"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
          <Button
            variant="outline"
            size="icon"
            className={`md:hidden rounded-sm h-11 w-11 shrink-0 ${isBlue ? "bg-transparent border-slate-600 text-slate-100 hover:bg-slate-800" : ""}`}
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open admin menu"
            data-testid="mgmt-mobile-menu-open"
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 min-w-0" data-testid="mgmt-main">
        <Outlet context={{ theme, setThemePref }} />
      </main>
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-[min(88vw,360px)] p-4 pt-[max(1rem,env(safe-area-inset-top))]" closeTestId="mgmt-mobile-menu-close" data-testid="mgmt-mobile-menu">
          <SheetHeader className="text-left pr-10 border-b border-border pb-4">
            <SheetTitle className="font-display">{bizName}</SheetTitle>
            <SheetDescription>Admin account, settings, installation, notifications, and sign out.</SheetDescription>
            <p className="text-xs text-muted-foreground break-all" data-testid="mgmt-mobile-account">{user?.name || user?.email}</p>
          </SheetHeader>
          <div className="py-4 space-y-2">
            {user?.role === "mgmt" && <Button variant="outline" className="w-full justify-start rounded-sm h-11" onClick={()=>{setMobileMenuOpen(false);setInvitationsOpen(true);}} data-testid="mgmt-mobile-invitations"><Users className="h-4 w-4 mr-3"/>Invite colony or manager</Button>}
            {isAdmin && (
              <Button variant="outline" className="w-full justify-start rounded-sm h-11" onClick={() => { setMobileMenuOpen(false); nav("/managementusa/admin/settings"); }} data-testid="mgmt-mobile-settings">
                <Settings className="w-4 h-4 mr-3" /> Settings
              </Button>
            )}
            {isAdmin && !isDemo && (
              <Button variant="outline" className="w-full justify-start rounded-sm h-11" onClick={() => { setMobileMenuOpen(false); nav("/desktop", { state: { admin: true } }); }} data-testid="mgmt-mobile-tools">
                <Wrench className="w-4 h-4 mr-3" /> Desktop tools
              </Button>
            )}
            {!isOwner && (
              <Button variant="outline" className="w-full justify-start rounded-sm h-11" onClick={() => { setMobileMenuOpen(false); setFeedbackOpen(true); }} data-testid="mgmt-mobile-report-issue">
                <Megaphone className="w-4 h-4 mr-3" /> Report issue
              </Button>
            )}
            <div className="grid grid-cols-1 gap-2 border-y border-border py-3 my-3">
              <InstallAppButton manifest="/manifest-mgmt.json" appleIcon="/mgmt-apple-touch.png" appName="Management USA NJ" iosTitle="Management" allowDesktop className="h-11 w-full justify-start" />
              <PushToggle client={api} base="/push" targetUrl="/managementusa/admin" testidPrefix="mgmt-mobile-push" alwaysVisible label="Phone notifications" className="h-11 w-full px-3 flex items-center justify-start gap-3" />
            </div>
            <Button variant="outline" className="w-full justify-start rounded-sm h-11 text-destructive hover:text-destructive" onClick={handleLogout} data-testid="mgmt-mobile-logout">
              <LogOut className="w-4 h-4 mr-3" /> Sign out
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} client={api} base="/feedback" />
      {user?.role === "mgmt" && <PortalInvitationsDialog open={invitationsOpen} onOpenChange={setInvitationsOpen}/>}
    </div>
  );
}
